import { FiRefreshCcw } from 'react-icons/fi'
import { MdSpeed } from 'react-icons/md'
import { RiLineChartLine, RiSettingsLine } from 'react-icons/ri'
import styles from '~/styles/pages/demo.css?url'
import formStyles from '~/styles/components/form.css?url'
import { FormBasics } from '~/components/forms/basics'
import { g2Reviews } from './_content.customer-support/g2-reviews'
import { ReviewCard } from '~/components/cards/review-card'
import { H1, H2, P, Stack } from '~/components-v2'
import {
  ActionData,
  handleHubspotFormAction,
  submitToHubspot,
} from '~/utils/hubspot'
import { formSchema } from '~/utils/form-schema'
import { HUBSPOT_PORTAL_ID, HUBSPOT_FORM_ID } from '~/config/form'
import { useActionData } from '@remix-run/react'
import reviewCardStyles from '~/components/cards/review-card/review-card.css?url'
import gridStyles from '~/styles/components/grid.css?url'
import { trialText } from '~/config'
import { ReactNode } from 'react'
import { LoaderFunctionArgs } from '@remix-run/node'
import { rudderStack } from '~/utils/rudder-stack.server'

export { meta } from '~/utils'
export const links = () => [
  { rel: 'stylesheet', href: formStyles },
  { rel: 'stylesheet', href: styles },
  { rel: 'stylesheet', href: reviewCardStyles },
  { rel: 'stylesheet', href: gridStyles },
]

const selectG2Reviews = [g2Reviews[0], g2Reviews[1], g2Reviews[2]]
export const G2Reviews = () => (
  <section className="demo-secondary-content grid cols3">
    {selectG2Reviews.map((review) => (
      <ReviewCard review={review} key={review.name} />
    ))}
  </section>
)

const { title, description, formButton, formTitle } = trialText

export const loader = async ({ request }: LoaderFunctionArgs) => {
  rudderStack(request, title)
  return { title, description }
}

export const action = handleHubspotFormAction({
  schema: formSchema,
  submitToHubspot,
  formId: HUBSPOT_FORM_ID,
  portalId: HUBSPOT_PORTAL_ID,
  thankYouUrl: '/thankyou',
})

const Headings = () => (
  <Stack gap={4} as="hgroup">
    <H1 color="title" lineHeight={1.15} fontWeight={800} fontSize="xxxl">
      {title}
    </H1>
    <H2 fontSize="lg" lineHeight={1.5} color="text4">
      {description}
    </H2>
  </Stack>
)

export default function Component({ children }: { children: ReactNode }) {
  const actionData = useActionData<ActionData>()
  return (
    <Stack gap={8} align="center">
      <div className="demo-page-content">
        <article className="content">
          <Headings />
          <dl>
            <dt>
              <RiLineChartLine />
              Rapid sharing
            </dt>
            <dd>Dashboards and visualizations are easy to build and share.</dd>
            <dt>
              <FiRefreshCcw />
              Evolve with every query
            </dt>
            <dd>
              Promote queries and metrics directly into the data model for
              frictionless governance.
            </dd>
            <dt>
              <RiSettingsLine />
              One platform for all
            </dt>
            <dd>
              Easy-to-use for anyone, regardless of skill level – use UI or
              hands-on SQL.
            </dd>
            <dt>
              <MdSpeed />
              Build fast
            </dt>
            <dd>Build a data model as you and the team query.</dd>
          </dl>
        </article>
        <article className="form form-narrow">
          <Stack gap={4}>
            <hgroup className="mobile-title">
              <Headings />
            </hgroup>
            <h4>{formTitle}</h4>
          </Stack>
          {children}
          <FormBasics
            buttonLabel={formButton}
            actionData={actionData}
            campaignCode="free-trial-cta"
          />
        </article>
      </div>

      <G2Reviews />
    </Stack>
  )
}

export function ErrorBoundary() {
  return (
    <Component>
      <P
        fontSize="xs"
        fontWeight={500}
        lineHeight={1.5}
        style={{ color: '#c00', paddingBottom: '1.5rem' }}
      >
        We encountered an error while processing your request. Please try again
      </P>
    </Component>
  )
}
